import { QN_ARTICLE_COUNT_PER_PAGE } from "@config/QuickNewsConstants";
import { logger } from "@src/logger/Logger";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import { MoreArticlesRequestData } from "@typings/MoreArticlesApi";
import { AdsCardProps, FeedItem, FinishCardProps } from "@typings/QuickNews";
import { useCallback, useMemo, useState } from "react";

import { getMoreFeeds } from "./QnLoadMore";

// --------------------------- Constants -------------------------------
export const END_CARD: FinishCardProps[] = [
  {
    id: `feed-end-success`,
    title: "Quick News Feed ended",
    name: "Quick News Feed ended",
    endCard: "Feeds Ended",
  },
];

// --------------------------- Ad Helpers ------------------------------
const generateAdItems = (count: number): AdsCardProps[] =>
  Array.from({ length: count }, (_, i) => ({
    id: `imu2-card-${i + 1}`,
    adIndex: i + 1,
    title: "IMU2",
    name: "IMU2",
    adType: "IMU2",
  }));

export const insertAds = (items: FeedItem[]): FeedItem[] => {
  const withAds: FeedItem[] = [];
  const adItems = generateAdItems(Math.ceil(items.length / 4));

  items.forEach((item, index) => {
    withAds.push(item);
    if ((index + 1) % 3 === 0 && adItems.length > 0) {
      withAds.push(adItems.shift() as AdsCardProps);
    }
  });

  return withAds;
};

// ------------------------- Infinite Content Hook ---------------------
interface UseInfiniteContentReturn {
  content: FeedItem[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  isInitialized: boolean;
  loadMoreOlder: () => void;
}

export const useInfiniteContent = ({
  Stories,
  initialId,
}: {
  Stories: ProcessedArticleData[];
  initialId?: string;
}): UseInfiniteContentReturn => {
  const initialContent = useMemo(() => {
    try {
      const merged = [...Stories];
      return insertAds(merged);
    } catch (err) {
      logger.error("Error computing initial content");
      return [];
    }
  }, [Stories]);

  const [articles, setArticles] = useState<ProcessedArticleData[]>(Stories);
  const [content, setContent] = useState<FeedItem[]>(initialContent);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreCount, setLoadMoreCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialized] = useState(true);
  const [loadOlderArticles, setLoadOlderArticles] = useState(false);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    setError(null);

    try {
      const newLoadMoreCount = loadMoreCount + 1;
      setLoadMoreCount(newLoadMoreCount);

      const lastArticle = articles.at(-1);
      const requestData: MoreArticlesRequestData = { sort: lastArticle?.sort };

      const newArticles = await getMoreFeeds(requestData, loadOlderArticles);
      setArticles((prev) => [...prev, ...newArticles]);

      setHasMore(newArticles.length === QN_ARTICLE_COUNT_PER_PAGE);

      let combinedItems: FeedItem[] = [...newArticles];

      // Exclude initial active item
      const excludedId = initialId || undefined;
      if (excludedId)
        combinedItems = combinedItems.filter((item) => item.id !== excludedId);

      setContent((prevContent) => {
        const updatedContent = prevContent.filter(
          (item) => !("adType" in item),
        );
        updatedContent.push(...combinedItems);

        return insertAds(updatedContent);
      });
      if (newArticles.length < QN_ARTICLE_COUNT_PER_PAGE)
        setContent((prev) => [...prev, ...END_CARD]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load more content",
      );
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, loadMoreCount, initialId, articles, loadOlderArticles]);

  const loadMoreOlder = useCallback(() => {
    setLoadOlderArticles(true);
    setHasMore(true);
  }, []);

  return {
    content,
    loading,
    error,
    hasMore,
    loadMore,
    isInitialized,
    loadMoreOlder,
  };
};

export function setMetaInfo(metaTags: {
  title?: string;
  description?: string;
  slug?: string;
}) {
  document.title = metaTags.title || "";

  const metaDescription = document.querySelector<HTMLMetaElement>(
    'meta[name="description"]',
  );
  if (metaDescription) {
    metaDescription.content = metaTags.description || "";
  }

  const metaCanonical = document.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (metaCanonical) {
    metaCanonical.href = `${window.location.origin}/${metaTags.slug || ""}`;
  }
}
